import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminCoursePages() {
  const [coursePages, setCoursePages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuFilter, setMenuFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCoursePages();
  }, []);

  const fetchCoursePages = async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in to manage course pages.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/course-pages`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setCoursePages(response.data.coursePages);
      } else {
        setError(response.data.message || 'Failed to fetch course pages.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching course pages.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      const response = await axios.delete(`${apiUrl}/api/admin/course-pages/${id}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        fetchCoursePages();
      } else {
        setError(response.data.message || 'Failed to delete course page.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting course page.');
    }
  };

  const menuOptions = useMemo(() => {
    return Array.from(new Set(coursePages.map((c) => c.menu_category))).sort();
  }, [coursePages]);

  const filteredPages = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return coursePages.filter((c) => {
      const matchesMenu = !menuFilter || c.menu_category === menuFilter;
      const matchesSearch = !term || c.title.toLowerCase().includes(term) || c.slug.toLowerCase().includes(term);
      return matchesMenu && matchesSearch;
    });
  }, [coursePages, menuFilter, searchTerm]);

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Course Pages</span>
                </li>
              </ul>
            </div>
            <Link to="/admin-course-pages/new" className="btn btn-main rounded-pill py-9">
              <i className="ph ph-plus me-2" /> Add Course Page
            </Link>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}

          <div className="card">
            <div className="card-header border-bottom border-gray-100 flex-between flex-wrap gap-12">
              <h5 className="mb-0">All Course Pages ({filteredPages.length})</h5>
              <div className="d-flex flex-wrap gap-12">
                <select
                  className="form-select w-auto"
                  value={menuFilter}
                  onChange={(e) => setMenuFilter(e.target.value)}
                >
                  <option value="">All Menus</option>
                  {menuOptions.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control w-auto"
                  placeholder="Search title or path..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : coursePages.length === 0 ? (
                <div className="text-center py-40 text-gray-400">
                  No course pages yet. Click "Add Course Page" to create your first one.
                </div>
              ) : filteredPages.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No course pages match your filters.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Title</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Menu</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Path</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Gallery</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Status</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPages.map((c) => (
                        <tr key={c.id}>
                          <td className="py-12 px-20">
                            <div className="d-flex align-items-center gap-12">
                              {c.cover_image ? (
                                <img
                                  src={`${apiUrl}/${c.cover_image}`}
                                  alt={c.title}
                                  style={{ width: 48, height: 36, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                                />
                              ) : (
                                <div style={{
                                  width: 48, height: 36, borderRadius: 6, background: '#f3f4f6',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                  <i className="ph ph-image text-gray-300" />
                                </div>
                              )}
                              <span className="text-15 fw-medium text-heading">{c.title}</span>
                            </div>
                          </td>
                          <td className="py-12 px-20">
                            <span className="badge bg-main-50 text-main-600 border-0 py-4 px-12 rounded-pill text-13 fw-medium">
                              {c.menu_category}
                            </span>
                            {c.sub_category && (
                              <div className="text-13 text-gray-400 mt-4">{c.sub_category}</div>
                            )}
                          </td>
                          <td className="py-12 px-20">
                            <code className="text-13">/{c.slug}</code>
                          </td>
                          <td className="py-12 px-20 text-14 text-gray-500">
                            {c.gallery_count > 0 ? `${c.gallery_count} item${c.gallery_count > 1 ? 's' : ''}` : '—'}
                          </td>
                          <td className="py-12 px-20">
                            <span className={`badge border-0 py-4 px-12 rounded-pill text-13 fw-medium ${c.status === 1 ? 'bg-success-100 text-success-600' : 'bg-warning-100 text-warning-600'}`}>
                              {c.status === 1 ? 'Published' : 'Draft'}
                            </span>
                          </td>
                          <td className="py-12 px-20">
                            <div className="d-flex align-items-center gap-8">
                              <a
                                href={`https://ifmedu.in/${c.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-main rounded-pill py-4 px-12"
                                title="View live page"
                              >
                                <i className="ph ph-arrow-square-out" />
                              </a>
                              <Link
                                to={`/admin-course-pages/edit/${c.id}`}
                                className="btn btn-sm btn-outline-main rounded-pill py-4 px-12"
                              >
                                <i className="ph ph-pencil me-1" /> Edit
                              </Link>
                              <button
                                type="button"
                                className="btn btn-sm rounded-pill py-4 px-12"
                                style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff' }}
                                onClick={() => handleDelete(c.id, c.title)}
                              >
                                <i className="ph ph-trash me-1" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminCoursePages;
